import React from 'react';
import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setModalName } from '@/lib/slices/modal-slice';
import ModalComponent from '@/common/components/modals/ModalComponent';
import Button from '@/common/components/ui/Button';
import { CancelIcon } from '@/assets/icons/button';
import MarkdownLinkedText from '@/common/components/MarkdownLinkedText';

interface TextSection {
  heading: string;
  body: string;
}

interface TextModalProps {
  modalName: string;
  title: string;
  version?: string;
  subtitle?: string;
  intro?: string;
  displayText: string;
  sections?: TextSection[];
  className?: string;
  closeOnClickOff?: boolean;
}

export default function TextModal({
  modalName,
  title,
  version,
  subtitle,
  intro,
  displayText,
  sections,
  className = 'max-w-[740px] w-full',
  closeOnClickOff = true,
}: TextModalProps) {
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const modalVisible: boolean = useAppSelector(() => store.getState().modal.visible);
  const currentModalName: string = useAppSelector(() => store.getState().modal.name);

  const handleClose = () => {
    dispatch(setModalName(''));
  };

  const isOpen = modalVisible && currentModalName === modalName;

  return (
    <ModalComponent
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel={title}
      contentSubtitle={subtitle}
      version={version}
      className={className}
      closeOnClickOff={closeOnClickOff}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 12 }}>
          <Button outline onClick={handleClose}>
            <CancelIcon className='w-[20px] h-[20px] mr-2' /> Close
          </Button>
        </div>
      }
    >
      {/* Numbered sections */}
      {sections && sections.length > 0 ? (
        <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {/* Intro paragraphs — scrolls together with sections */}
          {intro && (
            <div style={{ marginBottom: 16 }}>
              <MarkdownLinkedText
                text={intro}
                paragraphStyle={{
                  fontSize: 14,
                  color: '#374151',
                  lineHeight: '1.6',
                  margin: '0 0 12px 0',
                }}
                linkStyle={{
                  color: '#1570EF',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                }}
              />
            </div>
          )}

          {sections.map((section, i) => (
            <div key={i}>
              <div style={{ height: 1, background: '#E5E7EB' }} />
              <div
                style={{ padding: '20px 0', display: 'flex', alignItems: 'flex-start', gap: 16 }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: '#9CA3AF',
                    minWidth: 24,
                    paddingTop: 1,
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div style={{ flex: 1 }}>
                  <p
                    style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 6px 0' }}
                  >
                    {section.heading}
                  </p>
                  <div style={{ fontSize: 14, color: '#374151', lineHeight: '1.6', margin: 0 }}>
                    <MarkdownLinkedText
                      text={section.body}
                      paragraphStyle={{ margin: '0 0 10px 0' }}
                      linkStyle={{
                        color: '#1570EF',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        textUnderlineOffset: 2,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className='flex flex-col gap-2 bg-surface_gray_200 rounded-lg p-4 border border-border_gray_300 flex-1 min-h-0'>
          <div className='max-h-[65vh] overflow-y-auto pr-2 flex-1'>
            <pre className='font-[inherit] whitespace-pre-wrap text-base'>{displayText}</pre>
          </div>
        </div>
      )}
    </ModalComponent>
  );
}
